import React, { useEffect, useMemo, useState } from "react";
import { Bar } from "./CoTraderSkeletons";
import { useFxExecutionDecision } from "./Utilities/CoTraderUtils";
import { formatUserTimeWithZone } from "./Utilities/timezone";
import { useResolvedDark } from "../../ThemeProvider/ThemeContext";

const ALGO_COLOR = "#35C48B";
const RFQ_COLOR = "#E25B5B";
const DRAFT_AMOUNT_VALUE_KEY = "cotrader_fx_execution_amount_value_draft";
const DRAFT_AMOUNT_UNIT_KEY = "cotrader_fx_execution_amount_unit_draft";
const DRAFT_SIDE_KEY = "cotrader_fx_execution_side_draft";
const APPLIED_AMOUNT_VALUE_KEY = "cotrader_fx_execution_amount_value_applied";
const APPLIED_AMOUNT_UNIT_KEY = "cotrader_fx_execution_amount_unit_applied";
const APPLIED_SIDE_KEY = "cotrader_fx_execution_side_applied";
const LEGACY_DRAFT_AMOUNT_KEY = "cotrader_fx_execution_amount_draft";
const LEGACY_APPLIED_AMOUNT_KEY = "cotrader_fx_execution_amount_applied";

const AMOUNT_UNIT_OPTIONS = [
  { value: "thousand", label: "Thousand" },
  { value: "million", label: "Million" },
  { value: "billion", label: "Billion" },
];

const SIDE_OPTIONS = [
  { value: "buy_base", label: "Buy Base" },
  { value: "sell_base", label: "Sell Base" },
  { value: "buy_quote", label: "Buy Quote" },
  { value: "sell_quote", label: "Sell Quote" },
];

const normalizeStoredUnit = (rawUnit) => {
  const unit = String(rawUnit || "")
    .trim()
    .toLowerCase();

  if (unit === "b" || unit === "bn" || unit === "billion") return "billion";
  if (
    unit === "m" ||
    unit === "mm" ||
    unit === "mn" ||
    unit === "million"
  ) {
    return "million";
  }
  if (unit === "k" || unit === "thousand") return "thousand";

  return "million";
};

const inferAmountPartsFromNumeric = (value) => {
  if (!Number.isFinite(value) || value <= 0) {
    return { amountValue: "50", amountUnit: "million" };
  }

  if (value >= 1_000_000_000) {
    return {
      amountValue: String(Number((value / 1_000_000_000).toFixed(2))),
      amountUnit: "billion",
    };
  }

  if (value >= 1_000_000) {
    return {
      amountValue: String(Number((value / 1_000_000).toFixed(2))),
      amountUnit: "million",
    };
  }

  if (value >= 1_000) {
    return {
      amountValue: String(Number((value / 1_000).toFixed(2))),
      amountUnit: "thousand",
    };
  }

  return {
    amountValue: String(Number(value.toFixed(2))),
    amountUnit: "thousand",
  };
};

const parseLegacyAmountParts = (rawAmount) => {
  if (!rawAmount) {
    return { amountValue: "50", amountUnit: "million" };
  }

  const rawText = String(rawAmount).trim();
  const cleanedText = rawText.replace(/,/g, "").replace(/\$/g, "");
  const match = cleanedText.match(
    /(?<value>\d+(?:\.\d+)?)\s*(?<unit>billion|bn|b|million|mm|mn|m|thousand|k)?/i,
  );

  const parsedValue = Number(match?.groups?.value);
  const parsedUnit = normalizeStoredUnit(match?.groups?.unit);

  if (Number.isFinite(parsedValue) && match?.groups?.unit) {
    return {
      amountValue: String(parsedValue),
      amountUnit: parsedUnit,
    };
  }

  if (Number.isFinite(parsedValue)) {
    return inferAmountPartsFromNumeric(parsedValue);
  }

  return { amountValue: "50", amountUnit: "million" };
};

const composeTradeAmount = (amountValue, amountUnit) =>
  `${String(amountValue || "").trim()} ${normalizeStoredUnit(amountUnit)}`.trim();

const formatTimestamp = (timestamp) => formatUserTimeWithZone(timestamp);

const getConfidenceTone = (confidence) => {
  if (confidence == null) return "Preparing";
  if (confidence >= 80) return "High confidence";
  if (confidence >= 60) return "Moderate confidence";
  return "Measured confidence";
};

const CoTraderExecutionDecision = ({
  currencyPair,
  language = "English",
  isDark: isDarkProp,
}) => {
  const isDark = useResolvedDark(isDarkProp);
  const [tradeAmountValue, setTradeAmountValue] = useState(() => {
    const savedDraftValue = localStorage.getItem(DRAFT_AMOUNT_VALUE_KEY);
    const savedAppliedValue = localStorage.getItem(APPLIED_AMOUNT_VALUE_KEY);
    const legacyDraftAmount = localStorage.getItem(LEGACY_DRAFT_AMOUNT_KEY);
    const legacyAppliedAmount = localStorage.getItem(LEGACY_APPLIED_AMOUNT_KEY);

    if (savedDraftValue) return savedDraftValue;
    if (savedAppliedValue) return savedAppliedValue;

    return parseLegacyAmountParts(legacyDraftAmount || legacyAppliedAmount)
      .amountValue;
  });
  const [tradeAmountUnit, setTradeAmountUnit] = useState(() => {
    const savedDraftUnit = localStorage.getItem(DRAFT_AMOUNT_UNIT_KEY);
    const savedAppliedUnit = localStorage.getItem(APPLIED_AMOUNT_UNIT_KEY);
    const legacyDraftAmount = localStorage.getItem(LEGACY_DRAFT_AMOUNT_KEY);
    const legacyAppliedAmount = localStorage.getItem(LEGACY_APPLIED_AMOUNT_KEY);

    if (savedDraftUnit) return normalizeStoredUnit(savedDraftUnit);
    if (savedAppliedUnit) return normalizeStoredUnit(savedAppliedUnit);

    return parseLegacyAmountParts(legacyDraftAmount || legacyAppliedAmount)
      .amountUnit;
  });
  const [selectedSide, setSelectedSide] = useState(() => {
    const savedDraft = localStorage.getItem(DRAFT_SIDE_KEY);
    const savedApplied = localStorage.getItem(APPLIED_SIDE_KEY);
    return savedDraft || savedApplied || "buy_base";
  });
  const [submittedTradeAmountValue, setSubmittedTradeAmountValue] = useState(
    () => {
      const savedAppliedValue = localStorage.getItem(APPLIED_AMOUNT_VALUE_KEY);
      const legacyAppliedAmount = localStorage.getItem(LEGACY_APPLIED_AMOUNT_KEY);

      if (savedAppliedValue) return savedAppliedValue;

      return parseLegacyAmountParts(legacyAppliedAmount).amountValue;
    },
  );
  const [submittedTradeAmountUnit, setSubmittedTradeAmountUnit] = useState(
    () => {
      const savedAppliedUnit = localStorage.getItem(APPLIED_AMOUNT_UNIT_KEY);
      const legacyAppliedAmount = localStorage.getItem(LEGACY_APPLIED_AMOUNT_KEY);

      if (savedAppliedUnit) return normalizeStoredUnit(savedAppliedUnit);

      return parseLegacyAmountParts(legacyAppliedAmount).amountUnit;
    },
  );
  const [submittedTradeAmount, setSubmittedTradeAmount] = useState(() => {
    const savedAppliedValue = localStorage.getItem(APPLIED_AMOUNT_VALUE_KEY);
    const savedAppliedUnit = localStorage.getItem(APPLIED_AMOUNT_UNIT_KEY);
    const legacyAppliedAmount = localStorage.getItem(LEGACY_APPLIED_AMOUNT_KEY);

    if (savedAppliedValue && savedAppliedUnit) {
      return composeTradeAmount(savedAppliedValue, savedAppliedUnit);
    }

    const legacyParts = parseLegacyAmountParts(legacyAppliedAmount);
    return composeTradeAmount(legacyParts.amountValue, legacyParts.amountUnit);
  });
  const [submittedSide, setSubmittedSide] = useState(() => {
    return localStorage.getItem(APPLIED_SIDE_KEY) || "buy_base";
  });
  const [requestSeed, setRequestSeed] = useState(0);
  const [inputError, setInputError] = useState("");

  useEffect(() => {
    localStorage.setItem(DRAFT_AMOUNT_VALUE_KEY, tradeAmountValue);
  }, [tradeAmountValue]);

  useEffect(() => {
    localStorage.setItem(DRAFT_AMOUNT_UNIT_KEY, tradeAmountUnit);
  }, [tradeAmountUnit]);

  useEffect(() => {
    localStorage.setItem(DRAFT_SIDE_KEY, selectedSide);
  }, [selectedSide]);

  const {
    decision,
    explanation,
    loading,
    error,
    statusText,
    updatedAt,
  } = useFxExecutionDecision({
    currencyPair,
    tradeAmount: submittedTradeAmount,
    side: submittedSide,
    language,
    requestSeed,
    enabled: Boolean(currencyPair),
  });

  const theme = useMemo(
    () =>
      isDark
        ? {
            surface: "#12172A",
            pageBg: "#0B0F1A",
            border: "#232B4A",
            text: "#E8ECF8",
            muted: "#A6B0CF",
            trackBorder: "rgba(35,43,74,0.92)",
            trackFill:
              "linear-gradient(90deg, rgba(226,91,91,0.3) 0%, rgba(67,207,230,0.18) 50%, rgba(53,196,139,0.3) 100%)",
            sectionBg: "rgba(15,20,36,0.88)",
            badgeBg: "rgba(76,125,255,0.16)",
            badgeBorder: "rgba(76,125,255,0.32)",
            inputBg: "#0F1424",
            inputBorder: "#2B3558",
          }
        : {
            surface: "#FFFFFF",
            pageBg: "#FFFFFF",
            border: "#E9ECEF",
            text: "#0B1220",
            muted: "#5B6472",
            trackBorder: "rgba(233,236,239,0.95)",
            trackFill:
              "linear-gradient(90deg, rgba(226,91,91,0.18) 0%, rgba(67,207,230,0.12) 50%, rgba(53,196,139,0.18) 100%)",
            sectionBg: "#F8FAFC",
            badgeBg: "rgba(13,110,253,0.08)",
            badgeBorder: "rgba(13,110,253,0.18)",
            inputBg: "#FFFFFF",
            inputBorder: "#D8DEE6",
          },
    [isDark],
  );

  const normalizedTradeAmountValue = String(tradeAmountValue || "").trim();
  const normalizedTradeAmountUnit = normalizeStoredUnit(tradeAmountUnit);
  const normalizedSelectedSide = selectedSide.trim();
  const hasPendingChanges =
    normalizedTradeAmountValue !== submittedTradeAmountValue ||
    normalizedTradeAmountUnit !== submittedTradeAmountUnit ||
    normalizedSelectedSide !== submittedSide;

  const handleRunRecommendation = () => {
    if (!normalizedTradeAmountValue) {
      setInputError("Enter a trade amount to generate the recommendation.");
      return;
    }

    const numericTradeAmount = Number(normalizedTradeAmountValue);
    if (!Number.isFinite(numericTradeAmount) || numericTradeAmount <= 0) {
      setInputError("Enter a positive number for the trade amount.");
      return;
    }

    const composedTradeAmount = composeTradeAmount(
      normalizedTradeAmountValue,
      normalizedTradeAmountUnit,
    );

    setInputError("");
    setSubmittedTradeAmountValue(normalizedTradeAmountValue);
    setSubmittedTradeAmountUnit(normalizedTradeAmountUnit);
    setSubmittedTradeAmount(composedTradeAmount);
    setSubmittedSide(normalizedSelectedSide || "buy_base");
    localStorage.setItem(APPLIED_AMOUNT_VALUE_KEY, normalizedTradeAmountValue);
    localStorage.setItem(APPLIED_AMOUNT_UNIT_KEY, normalizedTradeAmountUnit);
    localStorage.setItem(LEGACY_APPLIED_AMOUNT_KEY, composedTradeAmount);
    localStorage.setItem(
      APPLIED_SIDE_KEY,
      normalizedSelectedSide || "buy_base",
    );
    setRequestSeed((current) => current + 1);
  };

  const handleAmountKeyDown = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    handleRunRecommendation();
  };

  const algoPercent =
    decision?.algo == null ? null : Math.round(decision.algo * 100);
  const rfqPercent =
    decision?.rfq == null ? null : Math.round(decision.rfq * 100);
  const sliderPosition =
    decision?.algo == null
      ? 50
      : Math.max(0, Math.min(100, decision.algo * 100));
  const recommendationColor =
    decision?.recommended === "rfq" ? RFQ_COLOR : ALGO_COLOR;
  const recommendationLabel =
    decision?.recommended === "rfq" ? "RFQ" : "Algo";

  const explanationEntries = Object.entries(explanation || {}).filter(
    ([title, detail]) => title && detail,
  );
  const recommendationSummary =
    explanationEntries.find(([title]) => title.toLowerCase() === "recommendation") ||
    explanationEntries[0] ||
    null;
  const supportingEntries = explanationEntries.filter(
    ([title, detail]) =>
      !recommendationSummary ||
      title !== recommendationSummary[0] ||
      detail !== recommendationSummary[1],
  );

  return (
    <div
      className="h-100 d-flex flex-column"
      style={{
        borderRadius: 16,
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        padding: 16,
        minHeight: 220,
      }}
    >
      <div
        style={{
          borderRadius: 12,
          background: theme.sectionBg,
          border: `1px solid ${theme.border}`,
          padding: 12,
        }}
      >
        <div
          style={{
            fontSize: "0.72rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: theme.muted,
            marginBottom: 10,
          }}
        >
          Trade Setup
        </div>

        <div className="d-flex flex-wrap gap-2 align-items-end">
          <div style={{ flex: "1 1 140px", minWidth: 120 }}>
            <label
              htmlFor="cotrader-execution-amount"
              style={{
                display: "block",
                fontSize: "0.76rem",
                fontWeight: 700,
                color: theme.muted,
                marginBottom: 6,
              }}
            >
              Amount
            </label>
            <input
              id="cotrader-execution-amount"
              type="number"
              min="0"
              step="any"
              value={tradeAmountValue}
              onChange={(event) => {
                setTradeAmountValue(event.target.value);
                if (inputError) setInputError("");
              }}
              onKeyDown={handleAmountKeyDown}
              placeholder="50"
              style={{
                width: "100%",
                height: 38,
                borderRadius: 10,
                border: `1px solid ${theme.inputBorder || theme.border}`,
                background: theme.inputBg || theme.surface,
                color: theme.text,
                padding: "0 12px",
                outline: "none",
              }}
            />
          </div>

          <div style={{ flex: "0 1 150px", minWidth: 130 }}>
            <label
              htmlFor="cotrader-execution-amount-unit"
              style={{
                display: "block",
                fontSize: "0.76rem",
                fontWeight: 700,
                color: theme.muted,
                marginBottom: 6,
              }}
            >
              Size
            </label>
            <select
              id="cotrader-execution-amount-unit"
              value={tradeAmountUnit}
              onChange={(event) => setTradeAmountUnit(event.target.value)}
              style={{
                width: "100%",
                height: 38,
                borderRadius: 10,
                border: `1px solid ${theme.inputBorder || theme.border}`,
                background: theme.inputBg || theme.surface,
                color: theme.text,
                padding: "0 12px",
                outline: "none",
              }}
            >
              {AMOUNT_UNIT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ flex: "0 1 170px", minWidth: 150 }}>
            <label
              htmlFor="cotrader-execution-side"
              style={{
                display: "block",
                fontSize: "0.76rem",
                fontWeight: 700,
                color: theme.muted,
                marginBottom: 6,
              }}
            >
              Side
            </label>
            <select
              id="cotrader-execution-side"
              value={selectedSide}
              onChange={(event) => setSelectedSide(event.target.value)}
              style={{
                width: "100%",
                height: 38,
                borderRadius: 10,
                border: `1px solid ${theme.inputBorder || theme.border}`,
                background: theme.inputBg || theme.surface,
                color: theme.text,
                padding: "0 12px",
                outline: "none",
              }}
            >
              {SIDE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className="btn btn-sm"
            onClick={handleRunRecommendation}
            disabled={loading}
            style={{
              height: 38,
              minWidth: 138,
              borderRadius: 10,
              background: loading ? `${ALGO_COLOR}99` : ALGO_COLOR,
              border: "1px solid transparent",
              color: "#FFFFFF",
              fontWeight: 700,
            }}
          >
            {loading ? "Updating..." : "Update Decision"}
          </button>
        </div>

        <div
          className="mt-2 d-flex flex-wrap justify-content-between gap-2"
          style={{ fontSize: "0.74rem", color: theme.muted }}
        >
          <span>
            Applied amount:{" "}
            <code>{composeTradeAmount(submittedTradeAmountValue, submittedTradeAmountUnit)}</code>
          </span>
          <span>{hasPendingChanges ? "Unsaved trade inputs" : "Inputs applied"}</span>
        </div>

        {inputError && (
          <div className="mt-2" style={{ fontSize: "0.76rem", color: RFQ_COLOR }}>
            {inputError}
          </div>
        )}
      </div>

      {loading && !decision && !error && (
        <div className="ct-decision-skeleton" role="status">
          <div className="d-flex justify-content-between align-items-start gap-3">
            <div>
              <Bar width={110} />
              <Bar width={180} height={24} style={{ marginTop: 10 }} />
              <Bar width={140} style={{ marginTop: 10 }} />
            </div>
            <Bar width={96} height={28} radius={999} />
          </div>
          {[92, 78, 86].map((width) => (
            <div
              key={width}
              className="d-flex justify-content-between align-items-center"
              style={{ marginTop: 16 }}
            >
              <Bar width={`${width / 2}%`} />
              <Bar width={64} />
            </div>
          ))}
          <div className="ct-decision-skeleton-status">
            {statusText || `Collecting execution context for ${currencyPair}.`}
          </div>
        </div>
      )}

      {error && !decision && (
        <div
          className="mt-3 d-flex flex-column justify-content-center align-items-center text-center"
          style={{
            borderRadius: 16,
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            minHeight: 220,
            padding: 24,
            color: theme.muted,
            gap: 8,
          }}
        >
          <div style={{ color: theme.text, fontWeight: 700 }}>
            Algo vs RFQ recommendation is unavailable.
          </div>
          <div>{error}</div>
        </div>
      )}

      {!loading && !error && !decision && (
        <div
          className="mt-3 d-flex flex-column justify-content-center align-items-center text-center"
          style={{
            borderRadius: 16,
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            minHeight: 220,
            padding: 24,
            color: theme.muted,
            gap: 8,
          }}
        >
          <div style={{ color: theme.text, fontWeight: 700 }}>
            No Algo vs RFQ recommendation yet.
          </div>
          <div>{statusText || "Update the trade inputs to generate a recommendation."}</div>
        </div>
      )}

      {decision && (
        <>
          <div
            className="mt-3 d-flex justify-content-between align-items-start gap-3 flex-wrap"
          >
            <div>
              <div
                style={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: theme.muted,
                }}
              >
                Algo vs RFQ
              </div>
              <div
                style={{
                  fontSize: "2rem",
                  lineHeight: 1.1,
                  fontWeight: 800,
                  color: theme.text,
                }}
              >
                {recommendationLabel}
              </div>
              <div style={{ fontSize: "0.82rem", color: theme.muted }}>
                {updatedAt == null
                  ? `Waiting for ${currencyPair} decision`
                  : `Last updated ${formatTimestamp(updatedAt)}`}
              </div>
            </div>

            <div
              style={{
                borderRadius: 999,
                padding: "8px 12px",
                fontSize: "0.78rem",
                fontWeight: 700,
                background: theme.badgeBg,
                color: theme.text,
                border: `1px solid ${theme.badgeBorder}`,
                minWidth: 118,
              }}
            >
              <div>{decision.confidence ?? "--"}% confidence</div>
              <div style={{ fontSize: "0.72rem", color: theme.muted }}>
                {getConfidenceTone(decision.confidence)}
              </div>
            </div>
          </div>

          <div className="mt-3">
            <div
              style={{
                position: "relative",
                height: 14,
                borderRadius: 999,
                overflow: "hidden",
                background: theme.trackFill,
                border: `1px solid ${theme.trackBorder}`,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: "50%",
                  width: 1,
                  background: theme.border,
                  transform: "translateX(-50%)",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: -4,
                  left: `${sliderPosition}%`,
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  transform: "translateX(-50%)",
                  background: recommendationColor,
                  border: `2px solid ${theme.pageBg}`,
                  boxShadow: "0 0 0 2px rgba(255,255,255,0.08)",
                }}
              />
            </div>

            <div
              className="d-flex justify-content-between align-items-center mt-2"
              style={{ fontSize: "0.78rem", color: theme.muted, gap: 12 }}
            >
              <span>RFQ {rfqPercent == null ? "--" : `${rfqPercent}%`}</span>
              <span>
                {decision.confidence == null
                  ? ""
                  : `${decision.confidence}% confidence`}
              </span>
              <span>Algo {algoPercent == null ? "--" : `${algoPercent}%`}</span>
            </div>
          </div>

          {recommendationSummary && (
            <div
              className="mt-3"
              style={{
                borderRadius: 12,
                padding: 14,
                background: `${recommendationColor}12`,
                border: `1px solid ${recommendationColor}2A`,
              }}
            >
              <div
                style={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: recommendationColor,
                  marginBottom: 6,
                }}
              >
                {recommendationSummary[0]}
              </div>
              <div style={{ color: theme.text, lineHeight: 1.5 }}>
                {recommendationSummary[1]}
              </div>
            </div>
          )}

          {supportingEntries.length > 0 && (
            <div className="mt-3 d-flex flex-column gap-2">
              {supportingEntries.map(([title, detail]) => (
                <div
                  key={title}
                  style={{
                    borderRadius: 12,
                    background: theme.sectionBg,
                    border: `1px solid ${theme.border}`,
                    padding: 12,
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.74rem",
                      fontWeight: 700,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      color: theme.muted,
                      marginBottom: 4,
                    }}
                  >
                    {title}
                  </div>
                  <div style={{ color: theme.text, lineHeight: 1.45 }}>
                    {detail}
                  </div>
                </div>
              ))}
            </div>
          )}

          {statusText && (
            <div
              className="mt-3"
              style={{ fontSize: "0.78rem", color: theme.muted }}
            >
              {statusText}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CoTraderExecutionDecision;
